import { Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import { Elements, Loader, TextButton, UploadFile } from '@tfo/common';
import fileDownload from 'js-file-download';
import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import $http from "../../../../utils/$http";
import CustomerPortalDialog from '../../common/CustomerPortalDialog';
import './GroupedOrderFileUpload.scss';

const { Paragraph } = Elements
const { Dropzone } = UploadFile

export type RequireGroupOrderFileProps = {
    highlighted?: boolean
    onFilesChange?: (groupOrderFiles: Array<string>) => void
}

export type FileModel = {
    originalFileName: string
    fileName: string
}

type GroupOrderFileState = "completed" | "progress" | "failed"
type GroupOrderFile = {
    key: string
    file: FileModel
    state: GroupOrderFileState
}

const GroupOrderFileUpload = (props: RequireGroupOrderFileProps) => {
    const [groupOrderFiles, setGroupOrderFiles] = React.useState<GroupOrderFile[]>([])

    useEffect(() => {
        getGroupOrderFiles()
    }, [])

    const getGroupOrderFiles = async () => {
        try {
            const response = await $http.get<FileModel[]>(`/file/group-order-files`)
            const files = response.map(file => {
                return {
                    key: file.fileName,
                    file: file,
                    state: "completed"
                } as GroupOrderFile
            })
            setGroupOrderFiles(files)

        }
        catch (e) {
            console.error(e)
        }
    }

    const setUploadState = (originalFileName: string, fileName: string, state: GroupOrderFileState) => {
        setGroupOrderFiles(groupOrderFile => {
            return [...groupOrderFile, {
                key: fileName,
                file: {
                    originalFileName: originalFileName,
                    fileName: fileName
                },
                state
            }]
        })
    }

    return (
        <div className="group-order-file-upload">
            <UploadDescription />
            <Upload {...props}
                uploadComplete={(fileName, fileKey) => setUploadState(fileName, fileKey, "completed")} />
            <UploadedFiles groupOrderFiles={groupOrderFiles} />
        </div>
    )
}

const UploadDescription = () => {
    const intl = useIntl()

    return (<>
        <Paragraph className='description-note' >
            {intl.formatMessage(
                { id: 'groupOrder.upload.note' },
                {
                    link: (
                        <a href={intl.formatMessage({ id: 'groupOrder.upload.note.link-url' })}
                            target='_blank'
                            rel="noreferrer">
                            {intl.formatMessage({ id: 'groupOrder.upload.note.link-text' })}
                        </a>
                    ),
                },
            )}
        </Paragraph>
    </>)
}

type ValidationError = {
    nationalId: string
    errors: string[]
}

const Upload = ({ ...events }: RequireGroupOrderFileProps & {
    uploadComplete: (fileName: string, fileKey: string) => void,
}) => {
    const intl = useIntl()
    const [submitError, setSubmitError] = React.useState<number | null>(null);
    const [validationErrors, setValidationErrors] = React.useState<ValidationError[]>([]);
    const [isLoading, setIsLoading] = React.useState(false);
    const [displayValidationErrors, setDisplayValidationErrors] = React.useState(false);

    const saveGroupOrderFile = async (file: File) => {
        setIsLoading(true)
        setSubmitError(null)
        setValidationErrors([])

        try {
            const formData = new FormData()
            formData.append('file', file)

            const response = await $http.post<string>(
                `/file/group-order-files`,
                formData,
                null, //no response content type expected
                'multipart/form-data')
            events.uploadComplete(file.name, response)

        }
        catch (e) {
            const error = e as {
                response?: {
                    status: number
                    data: {
                        errors: {
                            [key: string]: string[]
                        }
                    }
                }
            }

            console.log("validation error", { ...error })
            if (error.response === undefined) {
                // No response: could be offline, file locked, or other network error
                if (typeof navigator !== 'undefined' && navigator.onLine === false) {
                    setSubmitError(998) // Custom code for offline
                } else {
                    setSubmitError(999) // Unknown error, likely file locked
                }
            }
            else if (error.response.status === 400) {
                setSubmitError(400)
                if (error.response.data.errors) {
                    setValidationErrors(Object
                        .entries(error.response.data.errors)
                        .map(([nationalId, errors]) => ({ nationalId, errors } as ValidationError)))
                }
            }
            else if (error.response.status === 413 || error.response.status === 415) {
                setSubmitError(error.response.status)
            }
            else {
                setSubmitError(500)
            }
        }
        finally {
            setIsLoading(false)
        }
    }

    return (<>
        <div className={`group-order-file-drop-zone ${events.highlighted ? 'highlighted' : ''}`}>
            <Dropzone
                files={[]}
                onFilesAdded={(files) => saveGroupOrderFile(files[0])}
                submitError={!!submitError}
                disabled={false}
                single={true}
                textId='groupOrder.upload.text'
                subTextId='groupOrder.upload.subtext'
            />
            {submitError &&
                <>
                    <Paragraph className='upload-fail-message' small >
                        {intl.formatMessage(
                            { id: `groupOrder.upload-fail-${submitError}` },
                            {
                                details: str => {
                                    return validationErrors.length ? <a onClick={() => setDisplayValidationErrors(true)} >{str}</a> : <>{str}</>
                                }
                            })}
                    </Paragraph>
                </>
            }
            <ValidationErrorsDialog open={displayValidationErrors} onClose={() => setDisplayValidationErrors(false)} errors={validationErrors} />
        </div>
        {isLoading && <Loader />}
    </>);
}

const UploadedFiles = ({ groupOrderFiles }: {
    groupOrderFiles: GroupOrderFile[]
}) => {
    const [isDownloading, setIsDownloading] = useState<boolean>(false)
    const downloadAuthorizedFile = (e: React.MouseEvent, file: FileModel) => {
        e.preventDefault()
        setIsDownloading(true)
        $http.get<Blob>(`/file/group-order-files/${file.fileName}`, 'blob')
            .then(data => fileDownload(data, file.originalFileName))
            .finally(() => setIsDownloading(false))
    }

    const hasGroupOrderFiles = groupOrderFiles && groupOrderFiles.length > 0

    return !hasGroupOrderFiles
        ? null
        : <>
            <ul className='group-order-file-list'>
                {groupOrderFiles.map(groupOrderFile =>
                    <li key={groupOrderFile.key} className='download-file-link'>
                        {groupOrderFile.file.fileName &&
                            <a href={groupOrderFile.file.fileName} onClick={(e) => downloadAuthorizedFile(e, groupOrderFile.file)} >
                                <CheckIcon /> {groupOrderFile.file.originalFileName}
                            </a>}
                        {!groupOrderFile.file.fileName &&
                            <span>
                                <CheckIcon /> {groupOrderFile.file.originalFileName}
                            </span>}
                    </li>)}
            </ul>
            {isDownloading && <Loader />}
        </>
}

const ValidationErrorsDialog = ({ open, onClose, errors }:
    {
        open: boolean
        onClose: () => void
        errors: ValidationError[]
    }) => {
    const intl = useIntl()
    return (
        <>
            {
                <CustomerPortalDialog
                    open={open}
                    onClose={onClose}
                    title={intl.formatMessage({ id: 'groupOrder.upload-fail-400.details-header' })}
                    content={
                        <TableContainer className='group-order-file-validation-failed-dialog'>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell className="column-header">{intl.formatMessage({ id: 'groupOrder.upload-fail-400.details-header.source' })}</TableCell>
                                        <TableCell className="column-header">{intl.formatMessage({ id: 'groupOrder.upload-fail-400.details-header.errors' })}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {errors.map(ve =>
                                        <TableRow className="row" key={ve.nationalId}>
                                            <TableCell className="national-id" >{ve.nationalId}</TableCell>
                                            <TableCell className="errors" >
                                                {ve.errors.map((error, index) => {
                                                    const [message, detail] = error.split(':');
                                                    return <div key={index}>
                                                        {intl.formatMessage({ id: `groupOrder.upload-fail-400-${message}` }, { detail })}
                                                    </div>
                                                })}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    }
                    footer={
                        <Grid container spacing={2} justifyContent={'center'}>
                            <Grid item xs={6} md={4}>
                                <TextButton onClick={onClose} style={{ width: '100%' }}>
                                    {intl.formatMessage({ id: 'new.report.order.form.modal.duplicate.order.detected.confirm' })}
                                </TextButton>
                            </Grid>
                        </ Grid>
                    } />
            }
        </>
    )
}

export default GroupOrderFileUpload